import * as React from 'react';
import type { AgentRecordContext } from '@cdf/api-client-react';
import { AgentFooter } from './agent-footer';
import { browserSpeechSynthesisSupported, browserTranscriptionSupported, speakBrowserReply, startBrowserTranscription } from '@/lib/browser-voice';

interface Props {
  files?: React.ReactNode;
  errors?: readonly string[];
  context: AgentRecordContext;
  threadId?: string;
  disabled: boolean;
  blocked: boolean;
  hasDraft: boolean;
  reply?: { id: string; text: string };
  onDictation: (text: string) => void;
  onUtterance: (text: string) => Promise<string | null>;
  onInterrupt: () => void;
  onNewChat: () => void;
  onActiveChange: (active: boolean) => void;
}
/** Browser speech uses the same native helpers; interactive turns stay in the exact shared thread. */
export function SharedAgentFooter({ files, errors = [], context, threadId, disabled, blocked, hasDraft, reply, onDictation, onUtterance, onInterrupt, onNewChat, onActiveChange }: Props) {
  const [listening, setListening] = React.useState(false), [active, setActive] = React.useState(false);
  const [readAloud, setReadAloud] = React.useState(false), [error, setError] = React.useState<string>();
  const generation = React.useRef(0), stopDictation = React.useRef<(() => void) | null>(null);
  const voiceStop = React.useRef<(() => void) | null>(null), seen = React.useRef(reply?.id);
  const hintId = React.useId();
  const transcriptionSupported = browserTranscriptionSupported(), synthesisSupported = browserSpeechSynthesisSupported();
  const stop = React.useCallback(() => {
    generation.current++; stopDictation.current?.(); stopDictation.current = null; setListening(false);
    window.speechSynthesis?.cancel();
  }, []);
  React.useEffect(() => () => { stop(); onActiveChange(false); }, [stop, onActiveChange]);
  React.useEffect(() => { if (blocked) { stop(); setReadAloud(false); } }, [blocked, stop]);
  React.useEffect(() => { onActiveChange(listening || active); }, [listening, active, onActiveChange]);
  React.useEffect(() => {
    if (!reply || reply.id === seen.current) return;
    seen.current = reply.id;
    if (readAloud && !active && !blocked) speakBrowserReply(reply.text, setError);
  }, [reply?.id, reply?.text, readAloud, active, blocked]);
  function dictate() {
    voiceStop.current?.();
    if (listening) { stop(); return; }
    if (disabled || blocked || !transcriptionSupported) return;
    const token = ++generation.current;
    setError(undefined); setListening(true);
    stopDictation.current = startBrowserTranscription(text => {
      if (token === generation.current) onDictation(text);
    }, () => { if (token === generation.current) { generation.current++; stopDictation.current = null; setListening(false); } },
    message => { if (token === generation.current) setError(message); });
    if (!stopDictation.current) setListening(false);
  }
  return <>
    <AgentFooter files={files} errors={[...errors, error]} listening={listening} readAloud={readAloud} transcriptionSupported={transcriptionSupported} synthesisSupported={synthesisSupported}
      hintId={hintId} dictationDisabled={active || (!listening && (disabled || blocked || !transcriptionSupported))}
      newChatDisabled={disabled || blocked || active || listening}
      onDictation={dictate} onReadAloud={() => { setReadAloud(value => !value); if (readAloud) window.speechSynthesis?.cancel(); }}
      onStopReadAloud={() => { window.speechSynthesis?.cancel(); setReadAloud(false); }} onNewChat={onNewChat}
      availability={!threadId ? 'Send your first shared question or create a shared chat before starting interactive voice.' : 'Spoken questions and answers use this shared record chat. Private changes still require explicit review.'}
      voice={{ scope: { kind: 'entity', tenantId: context.tenantId, entityId: context.entityId }, context, conversationId: threadId,
        disabled: disabled || blocked || hasDraft || listening || !threadId, blocked,
        onConversation: () => undefined, onUtterance, onInterrupt, onActiveChange: setActive, stopRef: voiceStop }} />
  </>;
}
